import { Checkbox, FormControlLabel, Grid, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useState } from 'react';
import { UseFieldArrayReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NewOrderPartyType } from 'v2-types/order';
import { chargesPayeePayerForm } from '../services';

const useStyles = makeStyles((theme: any) => ({
  container: {
    backgroundColor: theme.palette.input.border,
    borderRadius: 8,
    padding: 16,
    flex: 1,
    [theme.breakpoints.up('md')]: {
      minWidth: 250,
      maxHeight: 400,
    },
  },
  clickeable: {
    width: '100%',
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    padding: 8,
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.palette.background.component,
    },
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: theme.palette.primary.main,
    },
  },
  stickySearch: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.input.border,
    paddingBottom: 8,
  },
  scrollArea: {
    overflow: 'scroll',
    scrollbarWidth: 'thin',
    [theme.breakpoints.up('md')]: {
      overflow: 'auto',
    },
  },
}));

interface Props {
  parties: NewOrderPartyType[];
  partiesSelected: UseFieldArrayReturn<chargesPayeePayerForm, 'InvolvedParties', 'id'>;
  onPartySelect: (partySelected: NewOrderPartyType) => void;
  kind: 'Payee' | 'Payer' | 'Tax' | 'Proprietor' | 'Ownership',
}

export const KnownPartiesList: FC<Props> = ({
  parties,
  partiesSelected,
  onPartySelect,
  kind,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [suggested, setSuggested] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');

  const byName = (a: NewOrderPartyType, b: NewOrderPartyType) => a.name.localeCompare(b.name);

  const query = searchText.toLowerCase();
  const desktopParties = parties.filter((party) => {
    const isSelected = partiesSelected.fields.some(({ party_id }) => party_id === party._id);
    if (isSelected) return true;

    const passesKindFilter = (() => {
      if (!suggested) return true;
      let regex = /^(underwriter|title company)$/i;
      if (kind === "Tax") regex = /^tax/i;
      if (kind === "Payer") regex = /^(buyer|seller)$/i;
      if (kind === "Proprietor") regex = /^seller/i;
      if (kind === "Ownership") regex = /^buyer/i;
      return party.kinds.some((k) => regex.test(k));
    })();

    if (!passesKindFilter) return false;
    if (!query) return true;
    return party.name.toLowerCase().includes(query) || party.kinds.some((k) => k.toLowerCase().includes(query));
  });

  const handleToggle = (party: NewOrderPartyType) => {
    const selectedIndex = partiesSelected.fields.findIndex(({ party_id }) => party_id === party._id);
    if (selectedIndex >= 0) {
      partiesSelected.remove(selectedIndex);
    } else {
      onPartySelect(party);
    }
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className={classes.container}
    >
      <Grid item container alignItems="center" justifyContent="space-between" wrap="nowrap" style={{ gap: "1rem" }}>
        <Typography variant="body1" style={{ fontWeight: 500, color: theme.palette.primary.light }}>
          {t('parties:parties')}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={suggested}
              onChange={(e) => setSuggested(e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={<Typography variant="caption">{t('parties:show-known-parties')}</Typography>}
          style={{ margin: 0 }}
        />
      </Grid>

      <Grid item style={{ marginTop: 8 }}>
        <Typography variant="caption" style={{ color: theme.palette.primary.light }}>
          {t('charges:mobileSelectedCount', { count: partiesSelected.fields.length })}
        </Typography>
      </Grid>

      {!isMobile && (
        <Grid item style={{ marginTop: 8 }}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            placeholder={t('charges:mobileSearchParties')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            inputProps={{ 'aria-label': t('charges:mobileSearchParties') }}
          />
        </Grid>
      )}

      <Grid className={classes.scrollArea}>
        {[...desktopParties].sort(byName).map((party) => {
          const isChecked = partiesSelected.fields.some(({ party_id }) => party_id === party._id);
          return (
            <Grid item key={party._id} style={{ marginTop: 4 }}>
              <button
                type="button"
                className={classes.clickeable}
                onClick={() => handleToggle(party)}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Checkbox
                  checked={isChecked}
                  color="primary"
                  size="small"
                  tabIndex={-1}
                  style={{ padding: 4 }}
                />
                <span>
                  <Typography variant="body2" style={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                    {party.name}
                  </Typography>
                  <Typography variant="caption">{party.kinds.join(", ")}</Typography>
                </span>
              </button>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};
